import { FormEvent, useState } from 'react';
import InlineScript from '../components/InlineScript';
import { Helmet } from 'react-helmet-async';

import { pageUrl } from '../config/site';
import { LOGO_ALT, LOGO_SRC } from '../constants/branding';
import { BEFORE_AFTER, WORK_SHOTS } from '../constants/projectGallery';
import Navigation from '../components/navigation';
import Footer from '../components/footer';
import { submitToMailer } from '../lib/submitToMailer';
import './request-a-free-quote.css';

export default function RequestAFreeQuote() {
  const [mailStatus, setMailStatus] = useState<
    'idle' | 'sending' | 'ok' | 'err'
  >('idle');
  const [mailMessage, setMailMessage] = useState('');

  async function handleQuoteSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setMailStatus('sending');
    setMailMessage('');
    const form = e.currentTarget;
    const fd = new FormData(form);
    const photos = form.querySelector<HTMLInputElement>('#photos');
    const result = await submitToMailer({
      form: 'quote',
      fullName: String(fd.get('full-name') ?? ''),
      phone: String(fd.get('phone') ?? ''),
      email: String(fd.get('email') ?? ''),
      address: String(fd.get('address') ?? ''),
      serviceType: String(fd.get('service-type') ?? ''),
      preferredDate: String(fd.get('preferred-date') ?? ''),
      description: String(fd.get('description') ?? ''),
      fileCount: photos?.files?.length ?? 0,
    });
    if (result.ok) {
      setMailStatus('ok');
      setMailMessage('Thanks — we received your quote request.');
      form.reset();
    } else {
      setMailStatus('err');
      setMailMessage(result.error);
    }
  }

  return (
    <div className="request-a-free-quote-container1">
      <Helmet>
        <title>Request a Free Quote | Shiny Touch Painting</title>
        <meta
          property="og:title"
          content="Request a Free Quote | Shiny Touch Painting"
        />
        <link rel="canonical" href={pageUrl('/request-a-free-quote')} />
        <meta property="og:url" content={pageUrl('/request-a-free-quote')} />
      </Helmet>
      <Navigation></Navigation>
      <section className="hero-quote-request">
        <div className="hero-media-bg">
          <video
            autoPlay
            muted
            loop
            playsInline
            src="https://videos.pexels.com/video-files/6474138/6474138-hd_720_1280_25fps.mp4"
            className="hero-video"
          ></video>
          <div className="request-a-free-quote-hero-overlay"></div>
        </div>
        <div className="hero-content-wrapper">
          <div className="hero-foreground-card">
            <div className="hero-brand-header">
              <div className="hero-logo-container">
                <img
                  src={LOGO_SRC}
                  alt={LOGO_ALT}
                  className="hero-logo-img"
                  width={140}
                  height={56}
                />
              </div>
              <div className="hero-badge-group">
                <span className="hero-badge">Professional</span>
                <span className="hero-badge">Reliable</span>
                <span className="hero-badge">Affordable</span>
              </div>
            </div>
            <h1 className="hero-title">
              Transform Your Space with a Shiny Touch
            </h1>
            <p className="hero-subtitle">
              Expert Interior &amp; Exterior Painting, Full Renovations, and
              Strata Maintenance in Burnaby &amp; Metro Vancouver. Request your
              free estimate today.
            </p>
            <div className="request-a-free-quote-hero-cta-group">
              <a href="#quick-quote-form">
                <div className="btn btn-primary btn-lg">
                  <span>Request a Free Quote</span>
                </div>
              </a>
              <div className="hero-thumbnail-preview">
                <div className="thumb-label">
                  <span>Latest Work</span>
                </div>
                <div className="thumb-container">
                  <img
                    src="https://images.pexels.com/photos/7217924/pexels-photo-7217924.jpeg?auto=compress&amp;cs=tinysrgb&amp;w=1500"
                    alt="Before and After Renovation"
                    className="thumb-image"
                  />
                  <div className="thumb-overlay">
                    <span>Before &amp; After</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="quick-quote-form" className="quote-form-section">
        <div className="quote-form-container">
          <div className="quote-form-card">
            <div className="quote-form-header">
              <h2 className="section-title">Quick Quote Details</h2>
              <p className="section-content">
                Tell us about your project and we&apos;ll provide a
                complimentary estimate within 24 hours.
              </p>
            </div>
            <form className="quote-main-form" onSubmit={handleQuoteSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="full-name" className="form-label">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="full-name"
                    name="full-name"
                    required
                    placeholder="John Doe"
                    data-form-field-id="full-name"
                    className="request-a-free-quote-form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    placeholder="(604) 000-0000"
                    data-form-field-id="phone"
                    className="request-a-free-quote-form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    data-form-field-id="email"
                    className="request-a-free-quote-form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="address" className="form-label">
                    Project Address
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    required
                    placeholder="Street, City, Postal Code"
                    data-form-field-id="address"
                    className="request-a-free-quote-form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="service-type" className="form-label">
                    Service Type
                  </label>
                  <select
                    id="service-type"
                    name="service-type"
                    required
                    defaultValue=""
                    data-form-field-id="service-type"
                    className="form-select"
                  >
                    <option value="" disabled>
                      Select a Service
                    </option>
                    <option value="interior">Interior Painting</option>
                    <option value="exterior">Exterior Painting</option>
                    <option value="renovation">Full Renovation</option>
                    <option value="strata">Strata Maintenance</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="preferred-date" className="form-label">
                    Preferred Start Date
                  </label>
                  <input
                    type="date"
                    id="preferred-date"
                    name="preferred-date"
                    data-form-field-id="preferred-date"
                    className="request-a-free-quote-form-input"
                  />
                </div>
                <div className="full-width form-group">
                  <label htmlFor="description" className="form-label">
                    Project Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    required
                    placeholder="Describe the scope of work, colors, or specific requirements..."
                    data-form-field-id="description"
                    className="form-textarea"
                  ></textarea>
                </div>
                <div className="full-width form-group">
                  <div className="file-upload-wrapper">
                    <label className="file-upload-label">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="upload-icon"
                      >
                        <path d="M12 3v12m5-7l-5-5l-5 5m14 7v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      </svg>
                      <span>Upload Project Photos (Optional)</span>
                      <input
                        type="file"
                        id="photos"
                        name="photos"
                        multiple
                        accept="image/*"
                        data-form-field-id="photos"
                        className="request-a-free-quote-file-input"
                      />
                    </label>
                    <div id="file-list" className="file-list-preview"></div>
                  </div>
                </div>
              </div>
              {mailMessage ? (
                <p
                  className="section-content"
                  style={{
                    marginBottom: '1rem',
                    color:
                      mailStatus === 'ok'
                        ? 'var(--color-primary)'
                        : 'var(--color-accent)',
                  }}
                  role="status"
                >
                  {mailMessage}
                </p>
              ) : null}
              <button
                type="submit"
                id="quote-form-submit"
                name="button"
                data-form-field-id="quote-form-submit"
                className="request-a-free-quote-btn-block btn btn-lg btn-accent"
                disabled={mailStatus === 'sending'}
              >
                {mailStatus === 'sending' ? 'Sending…' : 'Submit Quote Request'}
              </button>
            </form>
          </div>
        </div>
      </section>
      <section className="service-selector-section">
        <div className="service-selector-container">
          <div className="section-header-center">
            <h2 className="section-title">Define Your Project Scope</h2>
            <p className="section-content">
              Select a service to see how we categorize project complexity.
            </p>
          </div>
          <div className="service-grid">
            <div className="service-scope-card">
              <div className="request-a-free-quote-service-icon-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 2v2m4-2v4m3-4a1 1 0 0 1 1 1v9H6V3a1 1 0 0 1 1-1zM6 12a1 1 0 0 0-1 1v1a2 2 0 0 0 2 2h2a1 1 0 0 1 1 1v2.9a2 2 0 1 0 4 0V17a1 1 0 0 1 1-1h2a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1"></path>
                </svg>
              </div>
              <h3 className="service-card-title">Interior Painting</h3>
              <div className="scope-toggles">
                <button data-scope="small" className="scope-btn">
                  Small (1-2 Rooms)
                </button>
                <button data-scope="medium" className="scope-btn">
                  Medium (Whole Floor)
                </button>
                <button data-scope="large" className="scope-btn">
                  Large (Full House)
                </button>
              </div>
            </div>
            <div className="service-scope-card">
              <div className="request-a-free-quote-service-icon-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="16" height="6" x="2" y="2" rx="2"></rect>
                  <path d="M10 16v-2a2 2 0 0 1 2-2h8a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"></path>
                  <rect width="4" height="6" x="8" y="16" rx="1"></rect>
                </svg>
              </div>
              <h3 className="service-card-title">Exterior Painting</h3>
              <div className="scope-toggles">
                <button data-scope="small" className="scope-btn">
                  Small (Trim/Doors)
                </button>
                <button data-scope="medium" className="scope-btn">
                  Medium (Single Story)
                </button>
                <button data-scope="large" className="scope-btn">
                  Large (Multi-Level)
                </button>
              </div>
            </div>
            <div className="service-scope-card">
              <div className="request-a-free-quote-service-icon-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m14.622 17.897l-10.68-2.913M18.376 2.622a1 1 0 1 1 3.002 3.002L17.36 9.643a.5.5 0 0 0 0 .707l.944.944a2.41 2.41 0 0 1 0 3.408l-.944.944a.5.5 0 0 1-.707 0L8.354 7.348a.5.5 0 0 1 0-.707l.944-.944a2.41 2.41 0 0 1 3.408 0l.944.944a.5.5 0 0 0 .707 0zM9 8c-1.804 2.71-3.97 3.46-6.583 3.948a.507.507 0 0 0-.302.819l7.32 8.883a1 1 0 0 0 1.185.204C12.735 20.405 16 16.792 16 15"></path>
                </svg>
              </div>
              <h3 className="service-card-title">Full Renovation</h3>
              <div className="scope-toggles">
                <button data-scope="small" className="scope-btn">
                  Small (Bathroom)
                </button>
                <button data-scope="medium" className="scope-btn">
                  Medium (Kitchen)
                </button>
                <button data-scope="large" className="scope-btn">
                  Large (Whole Home)
                </button>
              </div>
            </div>
            <div className="service-scope-card">
              <div className="request-a-free-quote-service-icon-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                </svg>
              </div>
              <h3 className="service-card-title">Strata Maintenance</h3>
              <div className="scope-toggles">
                <button data-scope="small" className="scope-btn">
                  Small (Touch-ups)
                </button>
                <button data-scope="medium" className="scope-btn">
                  Medium (Common Areas)
                </button>
                <button data-scope="large" className="scope-btn">
                  Large (Full Complex)
                </button>
              </div>
            </div>
            <div className="service-scope-card decorative-card">
              <div className="request-a-free-quote-service-icon-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6L9 17l-5-5"></path>
                </svg>
              </div>
              <h3 className="service-card-title">Reliable Service</h3>
              <p className="service-card-text">
                Punctual and professional teams dedicated to your timeline.
              </p>
            </div>
            <div className="service-scope-card decorative-card">
              <div className="request-a-free-quote-service-icon-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 3v12m5-7l-5-5l-5 5m14 7v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                </svg>
              </div>
              <h3 className="service-card-title">Photo Analysis</h3>
              <p className="service-card-text">
                Upload photos below for a more accurate digital estimate.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="gallery-preview-section">
        <div className="gallery-header">
          <h2 className="section-title">Visual Proof &amp; Upload Preview</h2>
          <p className="section-content">
            See our recent transformations or preview your own uploads below.
          </p>
        </div>
        <div className="gallery-grid-full">
          <div className="gallery-item before-after">
            <img
              src={BEFORE_AFTER.project0.before}
              alt="Before — project one"
              className="gallery-img"
            />
            <div className="gallery-tag">
              <span>Before</span>
            </div>
          </div>
          <div className="gallery-item before-after">
            <img
              src={BEFORE_AFTER.project0.after}
              alt="After — project one"
              className="gallery-img"
            />
            <div className="gallery-tag after-tag">
              <span>After</span>
            </div>
          </div>
          <div className="gallery-item before-after">
            <img
              src={BEFORE_AFTER.project1.before}
              alt="Before — project two"
              className="gallery-img"
            />
            <div className="gallery-tag">
              <span>Before</span>
            </div>
          </div>
          <div className="gallery-item before-after">
            <img
              src={BEFORE_AFTER.project1.after}
              alt="After — project two"
              className="gallery-img"
            />
            <div className="gallery-tag after-tag">
              <span>After</span>
            </div>
          </div>
          <div id="upload-preview-1" className="gallery-item">
            <img
              src={WORK_SHOTS[0]}
              alt="Work on site"
              className="gallery-img"
            />
            <div className="gallery-tag">
              <span>On site</span>
            </div>
          </div>
          <div className="gallery-item">
            <img
              src={WORK_SHOTS[1]}
              alt="Work in progress"
              className="gallery-img"
            />
            <div className="gallery-tag">
              <span>In progress</span>
            </div>
          </div>
          <div className="gallery-item">
            <img
              src={WORK_SHOTS[2]}
              alt="Finished workmanship"
              className="gallery-img"
            />
            <div className="gallery-tag after-tag">
              <span>Finished</span>
            </div>
          </div>
        </div>
      </section>
      <section className="features-trust-section">
        <div className="features-container">
          <div className="section-header-left">
            <h2 className="section-title">Why Choose Shiny Touch</h2>
            <p className="section-content">
              We combine quality craftsmanship with a customer-first approach.
            </p>
          </div>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6L9 17l-5-5"></path>
                </svg>
              </div>
              <div className="feature-text">
                <h3 className="feature-title">Free Estimates</h3>
                <p className="feature-desc">
                  Detailed, transparent pricing with no hidden fees or
                  obligations.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                </svg>
              </div>
              <div className="feature-text">
                <h3 className="feature-title">Reliable</h3>
                <p className="feature-desc">
                  We show up on time and finish projects on schedule, every
                  time.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2v20m9-10H3"></path>
                </svg>
              </div>
              <div className="feature-text">
                <h3 className="feature-title">Affordable</h3>
                <p className="feature-desc">
                  Competitive rates without compromising on premium material
                  quality.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                </svg>
              </div>
              <div className="feature-text">
                <h3 className="feature-title">Professional</h3>
                <p className="feature-desc">
                  Licensed, insured, and highly trained specialists for all
                  renovations.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
              <div className="feature-text">
                <h3 className="feature-title">BC Location</h3>
                <p className="feature-desc">
                  Proudly serving Lower Mainland and the entire BC.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                </svg>
              </div>
              <div className="feature-text">
                <h3 className="feature-title">Strata Experts</h3>
                <p className="feature-desc">
                  Specialized maintenance services for strata and property
                  managers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="request-a-free-quote-testimonials-carousel-section">
        <div className="request-a-free-quote-testimonials-container testimonials-container">
          <div className="section-header-center">
            <h2 className="section-title">What Our Clients Say</h2>
          </div>
          <div className="carousel-wrapper">
            <div id="testimonial-track" className="carousel-track">
              <div className="request-a-free-quote-testimonial-card">
                <div className="testimonial-rating">
                  <span>★★★★★</span>
                </div>
                <p className="testimonial-quote">
                  &quot;The team at Shiny Touch was incredibly punctual and
                  professional. Our interior painting was done ahead of schedule
                  with flawless results.&quot;
                </p>
                <div className="request-a-free-quote-testimonial-author">
                  <span className="request-a-free-quote-author-name">
                    Sarah Jenkins
                  </span>
                  <span className="author-type">Residential Client</span>
                </div>
              </div>
              <div className="request-a-free-quote-testimonial-card">
                <div className="testimonial-rating">
                  <span>★★★★★</span>
                </div>
                <p className="testimonial-quote">
                  &quot;Highly recommend for strata maintenance. They handle our
                  complex&apos;s exterior with care and the pricing is very fair
                  for the quality provided.&quot;
                </p>
                <div className="request-a-free-quote-testimonial-author">
                  <span className="request-a-free-quote-author-name">
                    David Chen
                  </span>
                  <span className="author-type">Strata Council Member</span>
                </div>
              </div>
              <div className="request-a-free-quote-testimonial-card">
                <div className="testimonial-rating">
                  <span>★★★★★</span>
                </div>
                <p className="testimonial-quote">
                  &quot;Full renovation of our office space was handled
                  perfectly. Great communication throughout the entire process.
                  Best in Burnaby!&quot;
                </p>
                <div className="request-a-free-quote-testimonial-author">
                  <span className="request-a-free-quote-author-name">
                    Michael Ross
                  </span>
                  <span className="author-type">Commercial Property Owner</span>
                </div>
              </div>
            </div>
            <div className="carousel-controls">
              <button
                aria-label="Previous testimonial"
                className="request-a-free-quote-carousel-btn prev"
              >
                ←
              </button>
              <button
                aria-label="Next testimonial"
                className="request-a-free-quote-carousel-btn next"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </section>
      <section className="finalize-cta-section">
        <div className="finalize-container">
          <div className="finalize-card">
            <div className="finalize-content">
              <h2 className="section-title">Ready for Your Free Estimate?</h2>
              <p className="section-content">
                Our experts are standing by to discuss your painting or
                renovation project. No pressure, just professional advice.
              </p>
              <div className="finalize-actions">
                <div className="action-item">
                  <div className="action-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M8 2v4m8-4v4"></path>
                      <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                      <path d="M3 10h18"></path>
                    </svg>
                  </div>
                  <div className="action-text">
                    <span className="action-label">Schedule Consultation</span>
                    <a href="#">
                      <div className="btn btn-outline btn-sm">
                        <span>View Calendar</span>
                      </div>
                    </a>
                  </div>
                </div>
                <div className="action-item">
                  <div className="action-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </div>
                  <div className="action-text">
                    <span className="action-label">Immediate Contact</span>
                    <span className="action-value">(604) 555-0123</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="finalize-cta-wrapper">
              <a href="#quick-quote-form">
                <div className="btn btn-primary btn-xl">
                  <span>Get Started Now</span>
                </div>
              </a>
              <p className="cta-footer-text">
                99632 Market Crossing PO, Burnaby, BC V5J 0H7
              </p>
            </div>
          </div>
        </div>
      </section>
      <div className="request-a-free-quote-container2">
        <div className="request-a-free-quote-container3">
          <InlineScript
            html={`<style>
        @keyframes slideInLeft {from {opacity: 0;
transform: translateX(-40px);}
to {opacity: 1;
transform: translateX(0);}}
        </style> `}
          ></InlineScript>
        </div>
      </div>
      <div className="request-a-free-quote-container4">
        <div className="request-a-free-quote-container5">
          <InlineScript
            html={`<script defer data-name="quote-page-logic">
(function(){
  // Testimonial Carousel Logic (selectors must match button classes in this page)
  const track = document.getElementById("testimonial-track")
  const nextBtn = document.querySelector(".request-a-free-quote-carousel-btn.next")
  const prevBtn = document.querySelector(".request-a-free-quote-carousel-btn.prev")
  const slides = track ? Array.from(track.children) : []
  let currentIndex = 0

  if (track && nextBtn && prevBtn && slides.length > 0) {
    const updateCarousel = () => {
      const slideWidth = slides[0].getBoundingClientRect().width
      track.style.transform = \`translateX(-\${currentIndex * (slideWidth + 24)}px)\`
    }

    nextBtn.addEventListener("click", () => {
      currentIndex = (currentIndex + 1) % slides.length
      updateCarousel()
    })

    prevBtn.addEventListener("click", () => {
      currentIndex = (currentIndex - 1 + slides.length) % slides.length
      updateCarousel()
    })
  }

  // Scope Toggle Logic
  const scopeButtons = document.querySelectorAll(".scope-btn")
  scopeButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      const parentGroup = btn.closest(".scope-toggles")
      parentGroup.querySelectorAll(".scope-btn").forEach((b) => b.classList.remove("active"))
      btn.classList.add("active")
    })
  })

  // File Upload Preview
  const fileInput = document.getElementById("photos")
  const fileList = document.getElementById("file-list")
  const previewBox1 = document.getElementById("upload-preview-1")

  if (fileInput && fileList && previewBox1) {
    fileInput.addEventListener("change", (e) => {
      const files = e.target.files
      fileList.innerHTML = ""

      if (files && files.length > 0) {
        const fileNames = Array.from(files)
          .map((f) => f.name)
          .join(", ")
        fileList.textContent = \`Selected: \${fileNames}\`

        const reader = new FileReader()
        reader.onload = (event) => {
          previewBox1.innerHTML = \`<img src="\${event.target.result}" style="width:100%; height:100%; object-fit:cover;">\`
          previewBox1.classList.remove("placeholder-item")
        }
        reader.readAsDataURL(files[0])
      }
    })
  }

})()
</script>`}
          ></InlineScript>
        </div>
      </div>
      <Footer></Footer>
    </div>
  );
}
